import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SendIcon, CalendarClockIcon, BookmarkPlusIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { ScheduleState, SendMode } from '../../types/composer';
interface SendSchedulerProps {
  schedule: ScheduleState;
  onChange: (patch: Partial<ScheduleState>) => void;
  /** Label of the category the template would be filed under. */
  categoryLabel: string | null;
}
export function SendScheduler({
  schedule,
  onChange,
  categoryLabel
}: SendSchedulerProps) {
  const {
    t
  } = useLanguage();
  const options: {
    id: SendMode;
    icon: typeof SendIcon;
    title: string;
    description: string;
  }[] = [{
    id: 'now',
    icon: SendIcon,
    title: t('Envoyer maintenant', 'Send now'),
    description: t('Diffusion immédiate sur tous les canaux actifs.', 'Immediate delivery across every active channel.')
  }, {
    id: 'scheduled',
    icon: CalendarClockIcon,
    title: t("Programmer l'envoi", 'Schedule sending'),
    description: t('Choisissez la date et l’heure de diffusion.', 'Pick the delivery date and time.')
  }, {
    id: 'template',
    icon: BookmarkPlusIcon,
    title: t('Enregistrer comme modèle', 'Save as template'),
    description: t('Réutilisable pour vos prochains avis, sans envoi.', 'Reusable for your next notices, nothing is sent.')
  }];
  const today = new Date().toISOString().slice(0, 10);
  return <section className="space-y-6" data-id="element-4040" data-mp="1tmpwv8a55yid">
      <h2 className="text-xl font-semibold text-slate-900" data-id="element-4041" data-mp="1tln6d1gqdy2z">
        {t("Moment de l'envoi", 'Sending time')}
      </h2>

      <div className="bg-white rounded-xl border-[0.5px] border-slate-200 p-6 space-y-5" data-id="element-4042" data-mp="1yk866zs2mfqw">
        <div role="radiogroup" aria-label={t("Moment de l'envoi", 'Sending time')} className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-4043" data-mp="2malqu58btcbg">
          {options.map(opt => {
          const Icon = opt.icon;
          const active = schedule.mode === opt.id;
          return <button key={opt.id} type="button" role="radio" aria-checked={active} onClick={() => onChange({
            mode: opt.id
          })} className={`text-left p-4 rounded-xl border transition-colors ${active ? 'border-[#4F46E5] bg-indigo-50/40 ring-1 ring-[#4F46E5]' : 'border-slate-200 hover:border-[#4F46E5] hover:bg-slate-50'}`} data-id="element-4044" data-mp="2vzr86w2reskm">
                <span className={`inline-flex w-9 h-9 rounded-lg items-center justify-center mb-3 ${active ? 'bg-[#4F46E5] text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-4045" data-mp="287atmvi8diq2">
                  <Icon className="w-4 h-4" data-id="element-4046" data-mp="1u9dtxq8hcw76" />
                </span>
                <span className="block text-sm font-semibold text-slate-900" data-id="element-4047" data-mp="9rle1e82ekmw">
                  {opt.title}
                </span>
                <span className="block text-xs text-slate-500 mt-1 leading-snug" data-id="element-4048" data-mp="dsf2q9tc03gu">
                  {opt.description}
                </span>
              </button>;
        })}
        </div>

        <AnimatePresence mode="wait" data-id="element-4049" data-mp="nrmcyo4rrhzq">
          {schedule.mode === 'scheduled' && <motion.div key="scheduled" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-4050" data-mp="3m9i7km4pr8rc">
              <div className="pt-1 flex flex-col sm:flex-row gap-4" data-id="element-4051" data-mp="4srzdih1knl5">
                <div className="flex-1" data-id="element-4052" data-mp="33iidx7m9xarq">
                  <label htmlFor="send-date" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4053" data-mp="33hh04vdvia6b">
                    {t("Date d'envoi", 'Sending date')}
                  </label>
                  <input id="send-date" type="date" min={today} value={schedule.date} onChange={e => onChange({
                date: e.target.value
              })} className="block w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-4054" data-mp="1dz0mzfymyljd" />
                </div>
                <div className="w-full sm:w-44" data-id="element-4055" data-mp="1sphoezb9tbg9">
                  <label htmlFor="send-time" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4056" data-mp="26q27lwl3l9aa">
                    {t('Heure', 'Time')}
                  </label>
                  <input id="send-time" type="time" value={schedule.time} onChange={e => onChange({
                time: e.target.value
              })} className="block w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-4057" data-mp="cb80g6axxopy" />
                </div>
              </div>
            </motion.div>}

          {schedule.mode === 'template' && <motion.div key="template" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-4058" data-mp="2e88q3hwa75jf">
              <div className="pt-1 space-y-4" data-id="element-4059" data-mp="1raos3zp3ltbp">
                <div data-id="element-4060" data-mp="1vafmsehqwtli">
                  <label htmlFor="template-name" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4061" data-mp="1v9xm95o1vt7s">
                    {t('Nom du modèle', 'Template name')}
                  </label>
                  <input id="template-name" type="text" value={schedule.templateName} onChange={e => onChange({
                templateName: e.target.value
              })} placeholder={t('Ex. : Bris d’aqueduc — secteur nord', 'E.g. Water main break — north district')} className="block w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-4062" data-mp="3pmdycn3j21kk" />
                </div>
                <div data-id="element-4063" data-mp="1ce74nxpeb9o7">
                  <label htmlFor="template-description" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4064" data-mp="1cdf6cf4eyxu8">
                    {t('Description', 'Description')}
                    <span className="text-slate-400 font-normal" data-id="element-4065" data-mp="24w2njjgahsq1">
                      {' '}
                      · {t('optionnel', 'optional')}
                    </span>
                  </label>
                  <input id="template-description" type="text" value={schedule.templateDescription} onChange={e => onChange({
                templateDescription: e.target.value
              })} placeholder={t('Quand utiliser ce modèle ?', 'When should this template be used?')} className="block w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-4066" data-mp="27h0mp0ul9ewc" />
                </div>
                <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5" data-id="element-4067" data-mp="351wq8iou0dla">
                  {t('Classé dans la catégorie : ', 'Filed under category: ')}
                  <span className="font-semibold text-slate-700" data-id="element-4068" data-mp="3e4o0wg9vv5bv">
                    {categoryLabel ?? t('à choisir plus haut', 'to be chosen above')}
                  </span>
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}